import React, { useCallback, useEffect, useState } from 'react';

import type { UserType } from 'sl-sdk';
import { RolesEnum, rolesClassChecker } from 'sl-sdk';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';

import { SharelockApi } from 'api';
import { clearStoredJwt, getStoredJwt } from 'auth/storage';
import LoginView from 'components/LoginView';
import SidebarView from 'components/SidebarView';
import { resizeSidebar } from 'zaf/client';

const theme = createTheme({
  typography: {
    fontSize: 13,
  },
  components: {
    MuiButton: {
      defaultProps: {
        size: 'small',
      },
    },
  },
});

function parseJwtUserId(token: string): string | null {
  try {
    const base64Payload = token.split('.')[1];
    if (!base64Payload) {
      return null;
    }
    const json = atob(base64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    const decoded = JSON.parse(json) as { id?: string | number };
    return decoded.id != null ? String(decoded.id) : null;
  } catch {
    return null;
  }
}

const App: React.FC = () => {
  const [user, setUser] = useState<UserType | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  const logout = useCallback(async () => {
    try {
      await SharelockApi.auth.logout();
    } catch {
      // still clear local session if revoke fails
    }
    clearStoredJwt();
    setUser(null);
  }, []);

  const bootstrap = useCallback(async () => {
    setIsBootstrapping(true);
    const token = getStoredJwt();
    if (!token) {
      setUser(null);
      setIsBootstrapping(false);
      return;
    }

    const id = parseJwtUserId(token);
    if (!id) {
      void logout();
      setIsBootstrapping(false);
      return;
    }

    try {
      const session = await SharelockApi.auth.validateSession(id);
      if (session.auth === 'OK' && session.data && rolesClassChecker(session.data.roles, RolesEnum.Ops)) {
        setUser(session.data);
      } else {
        void logout();
      }
    } catch {
      void logout();
    } finally {
      setIsBootstrapping(false);
    }
  }, [logout]);

  useEffect(() => {
    void bootstrap();
    void resizeSidebar('420px');
  }, [bootstrap]);

  const renderContent = (): React.ReactNode => {
    if (isBootstrapping) {
      return null;
    }
    if (user) {
      return <SidebarView onLogout={logout} />;
    }
    return (
      <LoginView
        onAuthenticated={() => {
          void bootstrap();
        }}
      />
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {renderContent()}
    </ThemeProvider>
  );
};

export default App;
